import { FC, Fragment, useCallback } from "react";
import {
  CircularProgress,
  IconButton,
  Menu,
  Stack,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ProtectDialog } from "./menu-items/protect/ProtectDialog";
import { ActionsMenuItems } from "./menu-items/ActionsMenuItems";
import { UpcomingActionIcon } from "./UpcomingAction";
import { Protection } from "./Protection";
import { useDialog } from "../../../../../../../../../../../utils/hooks/useDialog.hook";
import { useMenuHook } from "../../../../../../../../../../../utils/hooks/useMenu.hook";
import {
  ResourceType,
  UpcomingAction,
} from "../../../../../../../../../../../services/cloudchipr.api";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../../../../../store/hooks";
import { resourcesProtectionLoadingSelector } from "../../../../../../../../../../../store/clean/selectors/resourcesProtectionLoadingSelector";
import { createSingleProtectThunk } from "../../../../../../../../../../../store/clean/thunks/createSingleProtectThunk";

interface ActionsMenuCellProps {
  resourceType: ResourceType;
  id: string;
  uniqueIdentifier: string;
  isProtected: boolean;
  inProgress?: boolean;
  unavailableActions?: string[];
  failureReason?: string;
  accountId: string;
  protectedBy: string | null;
  upcomingActions?: UpcomingAction[];
}

export const ActionsMenuCell: FC<ActionsMenuCellProps> = ({
  id,
  resourceType,
  isProtected,
  unavailableActions,
  inProgress,
  failureReason,
  uniqueIdentifier,
  accountId,
  upcomingActions,
  protectedBy,
}) => {
  const dispatch = useAppDispatch();

  const protectionLoading = useAppSelector((state) =>
    resourcesProtectionLoadingSelector(state, accountId, [id]),
  );
  const { anchor, open, openMenu, closeMenu } = useMenuHook();
  const {
    open: protectDialogOpen,
    openDialog: openProtectDialog,
    closeDialog: closeProtectDialog,
  } = useDialog();

  const closeProtectDialogHandler = useCallback(() => {
    closeProtectDialog();
    closeMenu();
  }, [closeMenu, closeProtectDialog]);

  const onProtectDialogConfirm = useCallback(async () => {
    await dispatch(
      createSingleProtectThunk({
        resourceType,
        id: id,
        isProtected,
      }),
    );
    closeProtectDialogHandler();
  }, [dispatch, id, isProtected, resourceType, closeProtectDialogHandler]);

  return (
    <Fragment>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.4}
        justifyContent="flex-end"
      >
        {inProgress && (
          <Tooltip title="Action in progress..." arrow>
            <CircularProgress
              size={20}
              color="warning"
              sx={{ verticalAlign: "middle", color: "info.main" }}
            />
          </Tooltip>
        )}

        {isProtected && <Protection protectedBy={protectedBy} />}

        {failureReason && !inProgress && (
          <Tooltip title={failureReason} arrow sx={{ display: "inline" }}>
            <WarningAmberIcon fontSize="small" color="error" />
          </Tooltip>
        )}

        <UpcomingActionIcon upcomingActions={upcomingActions} />

        <IconButton
          size="small"
          onClick={openMenu}
          disabled={inProgress}
          sx={{ p: 0 }}
        >
          <MoreVertIcon />
        </IconButton>
      </Stack>

      <Menu open={open} onClose={closeMenu} anchorEl={anchor}>
        <ActionsMenuItems
          id={id}
          uniqueIdentifier={uniqueIdentifier}
          isProtected={isProtected}
          inProgress={inProgress}
          unavailableActions={unavailableActions}
          closeMenu={closeMenu}
          resourceType={resourceType}
          openProtectDialog={openProtectDialog}
        />
      </Menu>

      {protectDialogOpen && (
        <ProtectDialog
          isProtected={isProtected}
          onClose={closeProtectDialogHandler}
          onConfirm={onProtectDialogConfirm}
          isLoading={protectionLoading}
        />
      )}
    </Fragment>
  );
};
